import React from "react";

import EventBus from "../../util/EventBus";
import {SessionContext} from "../../util/SessionContext";
import {availableModes} from "../../util/VisibilityModes";
import {Blue3, Grey6, Grey17} from "../../theme/styles";

/**
 * The persona a report is compiled under, as a tab row.
 *
 * ARRANGED LIKE TERMS AND RULES, deliberately and not merely similarly: same tab styling, same
 * position under the screen header, same row that carries other view controls on the right. A
 * reader who has used the Glossary already knows what this row is and where it lives, and Reporting
 * being the one screen that puts its persona somewhere else would be a thing to learn for no
 * reason.
 *
 * THE READOUT IS SEPARATE AND LIVES IN THE TITLE BAR. PersonaHeaderInfo says which persona is
 * active and which Position it resolves to; this row is how it is CHANGED. Terms and Rules split
 * those two jobs the same way, which is why the word "Scope" is gone -- it was doing both, and it
 * also collided with a field genuinely called Scope on the SOP form.
 *
 * CHANGING IT IS NOT A PREFERENCE. The mode travels as the visibility-mode header, the handler
 * refuses a persona this caller may not assume, and the compiler puts the platform's own visibility
 * predicate into the WHERE. It changes which records the numbers are computed from.
 *
 * HIDING AN UNGRANTED MODE IS UX, NOT ENFORCEMENT -- may_assume refuses one whatever the client
 * sends. availableModes is shared with the Data Catalog so the two cannot drift.
 */

export default function PersonaScope() {
    const sessionContext = new SessionContext();
    const active = sessionContext.getActiveVisibility();
    const activeMode = active?.Mode ?? "";

    const modes = availableModes(activeMode, sessionContext.getPrivileges(), sessionContext.getAccount());

    const selectMode = (mode: string) => {
        if (mode === activeMode) return;
        // THE VALUE IS CARRIED, NOT RE-CHOSEN. Widening from Position to Organization is a different
        // reading of the SAME persona: the server resolves the organization from the position.
        sessionContext.setActiveVisibility(mode, active?.Value ?? "");
        EventBus.visibility.emit("change-visibility", sessionContext.getActiveVisibility());
    };

    // Lifted verbatim from the Glossary so the two rows cannot drift apart visually.
    const tabStyle = (isActive: boolean) => ({
        cursor: "pointer", paddingBottom: 8, fontSize: 14,
        fontWeight: isActive ? 600 : 400, color: isActive ? Grey17 : Grey6,
        borderBottom: isActive ? `2px solid ${Blue3}` : "2px solid transparent",
    } as React.CSSProperties);

    // A LONE MODE IS NO CHOICE, and availableModes already returns none in that case. The title bar
    // still names the persona, so nothing is hidden -- the row simply stops pretending to offer
    // something.
    if (modes.length === 0) {
        return null;
    }

    return (
        <div style={{display: "flex", gap: 22}}>
            {modes.map(one => (
                <div key={one.mode}
                     onClick={() => selectMode(one.mode)}
                     aria-label={`Report as ${one.label}`}
                     style={tabStyle(activeMode === one.mode)}>
                    {one.label}
                </div>
            ))}
        </div>
    );
}
